import React, { useEffect, useState, useRef } from 'react';
import { Search as SearchIcon, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
interface SearchResult {
  title: string;
  description: string;
  path: string;
  category: string;
}
export function Search() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  // Search content index
  const searchIndex: SearchResult[] = [{
    title: 'About Us',
    description: 'Learn about our mission, vision, and the values that drive our work',
    path: '/about',
    category: 'About'
  }, {
    title: 'Our Programs',
    description: 'Discover our initiatives in education, healthcare, and community development',
    path: '/programs',
    category: 'Programs'
  }, {
    title: 'Impact',
    description: "See how we're making a difference in communities across Nigeria",
    path: '/impact',
    category: 'Impact'
  }, {
    title: 'Success Stories',
    description: 'Read inspiring stories of transformation and hope',
    path: '/stories',
    category: 'Stories'
  }, {
    title: 'Organizational Structure',
    description: 'Meet our leadership team and understand our governance',
    path: '/structure',
    category: 'About'
  }, {
    title: 'Get Involved',
    description: 'Volunteer, partner, or support our mission',
    path: '/get-involved',
    category: 'Get Involved'
  }, {
    title: 'Donate',
    description: 'Support our work and transform lives',
    path: '/donate',
    category: 'Support'
  }, {
    title: 'Education Support',
    description: 'Scholarships and educational resources for underprivileged students',
    path: '/programs',
    category: 'Programs'
  }, {
    title: 'Healthcare Access',
    description: 'Medical outreach and health education initiatives',
    path: '/programs',
    category: 'Programs'
  }, {
    title: 'Women & Youth Empowerment',
    description: 'Skills training and economic opportunities',
    path: '/programs',
    category: 'Programs'
  }];
  // Handle search
  useEffect(() => {
    if (query.trim().length > 1) {
      const filtered = searchIndex.filter(item => item.title.toLowerCase().includes(query.toLowerCase()) || item.description.toLowerCase().includes(query.toLowerCase()) || item.category.toLowerCase().includes(query.toLowerCase()));
      setResults(filtered);
    } else {
      setResults([]);
    }
  }, [query]);
  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);
  // Handle keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Cmd+K or Ctrl+K to open search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      // Escape to close
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);
  const handleResultClick = (path: string) => {
    navigate(path);
    setIsOpen(false);
    setQuery('');
  };
  return <>
      {/* Search Button */}
      <button onClick={() => setIsOpen(true)} className="flex items-center gap-2 px-3 py-2 rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-600 hover:text-charcoal transition-all duration-300" aria-label="Search" data-id="element-315">
        <SearchIcon size={18} data-id="element-316" />
        <span className="hidden md:inline text-sm" data-id="element-317">Search</span>
        <kbd className="hidden md:inline px-2 py-0.5 text-xs bg-white rounded border border-neutral-300" data-id="element-318">
          ⌘K
        </kbd>
      </button>

      {/* Search Modal */}
      {isOpen && <div className="fixed inset-0 z-[100] flex items-start justify-center pt-20 px-4 bg-charcoal/50 backdrop-blur-sm animate-fadeIn" data-id="element-319">
          <div ref={searchRef} className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden animate-scaleIn" data-id="element-320">
            {/* Search Input */}
            <div className="flex items-center gap-3 p-4 border-b border-neutral-200" data-id="element-321">
              <SearchIcon size={20} className="text-neutral-400" data-id="element-322" />
              <input ref={inputRef} type="text" placeholder="Search for programs, stories, or information..." value={query} onChange={e => setQuery(e.target.value)} className="flex-1 text-lg outline-none bg-transparent" aria-label="Search input" data-id="element-323" />
              <button onClick={() => setIsOpen(false)} className="p-2 rounded-full hover:bg-neutral-100 transition-colors" aria-label="Close search" data-id="element-324">
                <X size={20} className="text-neutral-400" data-id="element-325" />
              </button>
            </div>

            {/* Search Results */}
            <div className="max-h-96 overflow-y-auto" data-id="element-326">
              {query.trim().length > 1 ? results.length > 0 ? <div className="p-2" data-id="element-327">
                    {results.map((result, index) => <button key={index} onClick={() => handleResultClick(result.path)} className="w-full text-left p-4 rounded-lg hover:bg-neutral-50 transition-colors group" data-id="element-328">
                        <div className="flex items-start justify-between gap-4" data-id="element-329">
                          <div className="flex-1" data-id="element-330">
                            <div className="flex items-center gap-2 mb-1" data-id="element-331">
                              <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full" data-id="element-332">
                                {result.category}
                              </span>
                            </div>
                            <h3 className="font-semibold text-charcoal group-hover:text-primary transition-colors mb-1" data-id="element-333">
                              {result.title}
                            </h3>
                            <p className="text-sm text-neutral-600 line-clamp-2" data-id="element-334">
                              {result.description}
                            </p>
                          </div>
                          <SearchIcon size={16} className="text-neutral-300 group-hover:text-primary transition-colors flex-shrink-0 mt-1" data-id="element-335" />
                        </div>
                      </button>)}
                  </div> : <div className="p-8 text-center text-neutral-500" data-id="element-336">
                    <SearchIcon size={48} className="mx-auto mb-4 opacity-20" data-id="element-337" />
                    <p data-id="element-338">No results found for "{query}"</p>
                    <p className="text-sm mt-2" data-id="element-339">
                      Try different keywords or browse our pages
                    </p>
                  </div> : <div className="p-8 text-center text-neutral-500" data-id="element-340">
                  <SearchIcon size={48} className="mx-auto mb-4 opacity-20" data-id="element-341" />
                  <p data-id="element-342">Start typing to search...</p>
                  <p className="text-sm mt-2" data-id="element-343">
                    Search for programs, stories, or information
                  </p>
                </div>}
            </div>

            {/* Quick Links */}
            {query.trim().length === 0 && <div className="border-t border-neutral-200 p-4 bg-neutral-50" data-id="element-344">
                <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide mb-3" data-id="element-345">
                  Quick Links
                </p>
                <div className="grid grid-cols-2 gap-2" data-id="element-346">
                  {['About', 'Programs', 'Donate', 'Get Involved'].map(link => <button key={link} onClick={() => handleResultClick(`/${link.toLowerCase().replace(' ', '-')}`)} className="px-3 py-2 text-sm text-left rounded-lg hover:bg-white transition-colors" data-id="element-347">
                        {link}
                      </button>)}
                </div>
              </div>}
          </div>
        </div>}
    </>;
}